import type { FC, ReactNode } from 'react'

import {
    CircleCheck,
    EllipsisVertical,
    Share,
    SquarePlus
} from 'lucide-react'
import { Button } from '@/components/kit/button'
import { Sheet } from '@/components/kit/sheet'
import { useToast } from '@/components/kit/toast'
import { useInstallPrompt } from '@/hooks/use-install-prompt'
import { BrandMark } from '@/components/app/brand-mark'

interface InstallSheetProps {
    open: boolean
    onClose: () => void
}

const Step: FC<{ index: number, children: ReactNode }> = ({ index, children }): ReactNode => {
    return (
        <li className='flex items-start gap-3'>
            <span className='grid size-7 shrink-0 place-items-center rounded-full bg-accent text-[13px] font-bold text-accent-foreground'>
                {index}
            </span>

            <span className='pt-0.5 text-sm leading-relaxed text-foreground'>
                {children}
            </span>
        </li>
    )
}

export const InstallSheet: FC<InstallSheetProps> = ({ open, onClose }): ReactNode => {
    const { state, install } = useInstallPrompt()
    const toast = useToast()

    const onInstall = async () => {
        const accepted = await install()

        if (accepted) {
            toast({ title: 'Washline is installed', description: 'Open it from your home screen like any other app.' })
            onClose()
        }
    }

    return (
        <Sheet
            open={open}
            onClose={onClose}
            title='Install on this device'
            description='Use Washline full screen from your home screen, even when the internet drops.'
            size='sm'
            footer={state === 'available'
                ? (
                    <Button size='lg' className='w-full' onClick={onInstall}>
                        Install app
                    </Button>
                )
                : (
                    <Button size='lg' variant='secondary' className='w-full' onClick={onClose}>
                        Done
                    </Button>
                )}
        >
            <div className='flex items-center gap-3 rounded-xl border border-border bg-surface p-3'>
                <BrandMark className='size-12' />

                <div>
                    <p className='font-heading font-bold text-foreground'>
                        Washline
                    </p>

                    <p className='text-[13px] text-muted-foreground'>
                        Car wash operations
                    </p>
                </div>
            </div>

            <div className='mt-5'>
                {state === 'installed' && (
                    <p className='flex items-center gap-2 text-sm font-semibold text-emerald-700'>
                        <CircleCheck className='size-5' aria-hidden='true' />
                        Already installed on this device.
                    </p>
                )}

                {state === 'available' && (
                    <p className='text-sm leading-relaxed text-muted-foreground'>
                        Tap Install app and confirm. The app icon appears on your home screen.
                    </p>
                )}

                {state === 'ios' && (
                    <ol className='space-y-3'>
                        <Step index={1}>
                            Open this page in Safari and tap the Share button
                            <Share className='mx-1 inline size-4 align-[-3px]' aria-label='Share' />
                            at the bottom of the screen.
                        </Step>

                        <Step index={2}>
                            Scroll down and tap
                            <span className='mx-1 inline-flex items-center gap-1 font-semibold'>
                                <SquarePlus className='size-4' aria-hidden='true' />
                                Add to Home Screen
                            </span>
                        </Step>

                        <Step index={3}>
                            Tap Add. Washline now opens full screen from its icon.
                        </Step>
                    </ol>
                )}

                {(state === 'manual' || state === 'unknown') && (
                    <ol className='space-y-3'>
                        <Step index={1}>
                            Open this page in Chrome or Edge on your phone or computer.
                        </Step>

                        <Step index={2}>
                            Open the browser menu
                            <EllipsisVertical className='mx-1 inline size-4 align-[-3px]' aria-label='menu' />
                            and choose Install app or Add to Home screen.
                        </Step>

                        <Step index={3}>
                            Confirm, then open Washline from its icon.
                        </Step>
                    </ol>
                )}
            </div>
        </Sheet>
    )
}
